import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import useDirectory from "../hooks/useDirectory";
import useCustomerBadges from "../hooks/useCustomerBadges";
import usePipelineBoard from "../hooks/usePipelineBoard";
import PipelineCard from "../components/pipeline/PipelineCard";
import QuickPanel from "../components/pipeline/QuickPanel";
import useViewportWidth from "../hooks/useViewportWidth";
import { panelMode, ficheNavigation } from "../lib/quickPanel";
import { layoutOf, activeIndex, sheetFilterCount, COLUMN_GAP } from "../lib/pipelineLayout";
import StageTabs from "../components/pipeline/StageTabs";
import CountersBand from "../components/pipeline/CountersBand";
import { QuickNoteDialog, QuickAssignDialog, ReopenDialog } from "../components/pipeline/QuickDialogs";
import TaskFormSheet from "../components/TaskFormSheet";
import api from "../lib/api";
import { errorMessage, conflictInfo } from "../lib/errorMessage";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { STAGES, STAGE_LABEL, STAGE_GROUPS, SYSTEM_MANAGED_STAGES, ACQUISITION_CHANNELS } from "../lib/pipeline";
import { REOPEN_STAGES } from "../lib/pipelineCard";
import {
  PHASES, SCOPES, SORTS, flagLabel, parseView, pipelineToProspects, readMemory, writeMemory, resolveView, visibleColumns, phaseOf,
} from "../lib/prospectViews";
import StatusExtraFields, { defaultStatusExtra, isStatusExtraValid, buildStatusExtraPayload } from "../components/StatusExtraFields";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search, Columns3, Plus, ArrowRight, X, AlertCircle, Lock, ListFilter, Loader2, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  ResponsivePopover as Popover, ResponsivePopoverContent as PopoverContent, ResponsivePopoverTrigger as PopoverTrigger,
} from "../components/ui/responsive-popover";

const SEARCH_DEBOUNCE_MS = 300;
const SKIPPED_LABELS = { status: "Statut", region: "Région", source: "Source", late: "À faire aujourd'hui ou en retard", noaction: "Aucune action visible" };
const PILOT_OPTIONS = [{ id: "all", label: "Pilote : tous" }, { id: "yes", label: "Avec un pilote" }, { id: "no", label: "Sans pilote" }];
const todayIso = () => new Date().toISOString().slice(0, 10);
const plural = (n, one, many) => `${n} ${n > 1 ? many : one}`;

function Segmented({ label, value, options, onChange, testid, scroll }) {
  return (
    <div role="group" aria-label={label} data-testid={testid} className={`rounded-lg border border-slate-200 bg-white p-0.5 ${scroll ? "flex max-w-full overflow-x-auto whitespace-nowrap scrollbar-thin" : "inline-flex flex-wrap"}`}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          aria-pressed={value === o.id}
          onClick={() => onChange(o.id)}
          data-testid={`${testid}-${o.id}`}
          className={`shrink-0 px-3 h-8 rounded-md text-sm font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-jamora [@media(pointer:coarse)]:h-11 ${
            value === o.id ? "bg-jamora text-white" : "text-slate-700 hover:bg-slate-100"
          }`}
        >
          {o.label}{o.count !== undefined && o.count !== null ? <span className={`ml-1.5 font-mono text-xs ${value === o.id ? "text-white/90" : "text-slate-500"}`}>{o.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

function FilterSelect({ native, label, testid, className, value, onChange, options }) {
  if (native) {
    return (
      <select
        aria-label={label} data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="h-11 w-full rounded-md border border-slate-200 bg-white px-3 text-base text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-jamora"
      >
        {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    );
  }
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`w-full h-9 ${className}`} aria-label={label} data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

const COLUMN_SIZE = {
  desktop: "min-w-[280px] w-[280px]",
  tablet: "min-w-0 w-[calc((100%-1rem)/2)]",
  phone: "min-w-0 w-full",
};

export default function Pipeline() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const can = {
    changeStatus: hasPermission(user, "prospects:change_status"),
    addInteraction: hasPermission(user, "prospects:add_interaction"),
    createTask: hasPermission(user, "tasks:create"),
    manageOnboarding: hasPermission(user, "prospects:manage_onboarding"),
  };
  const canChangeStatus = can.changeStatus;
  const canCreateProspect = hasPermission(user, "prospects:create");
  const canImport = hasPermission(user, "csv:import");
  const canAssign = hasPermission(user, "prospects:assign");
  const canScopeAll = hasPermission(user, "prospects:scope_all");
  const canViewTasks = hasPermission(user, "tasks:view");
  const canScopeAllTasks = hasPermission(user, "tasks:scope_all");
  const directory = useDirectory(canAssign || canScopeAllTasks || canScopeAll);
  const customerBadges = useCustomerBadges(user);

  const [memory, setMemory] = useState(() => readMemory(user?.id));
  const urlView = useMemo(() => parseView(searchParams), [searchParams]);
  const resolved = resolveView(urlView, memory, { canScopeAll });
  const view = { ...resolved, scope: resolved.scope || "me" };
  const { phase, scope, sort } = view;
  const stageIds = useMemo(() => phaseOf(phase).stageIds, [phase]);
  const shownStageIds = useMemo(() => visibleColumns(phase, memory.columns), [phase, memory.columns]);

  const viewportWidth = useViewportWidth();
  const ficheId = urlView.fiche || null;
  const mode = panelMode(viewportWidth);
  const layout = layoutOf(viewportWidth);
  const isPhone = layout === "phone";
  const snapBoard = layout !== "desktop";
  const boardRef = useRef(null);
  const [activeCol, setActiveCol] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // The phone sheet edits a draft and applies it once: any address change closes an open overlay (lib/overlayHistory.js), so a filter
  // written to the address at each choice would shut the sheet under the person's finger.
  const [draft, setDraft] = useState({});
  const stagesKey = shownStageIds.join(",");
  const columnStep = () => (boardRef.current?.firstElementChild?.getBoundingClientRect().width || 0) + COLUMN_GAP;
  // The scroller shows the first column again whenever the set of columns or the layout changes.
  useEffect(() => { setActiveCol(0); if (boardRef.current) boardRef.current.scrollLeft = 0; }, [stagesKey, layout]);
  const onBoardScroll = () => {
    const el = boardRef.current;
    if (!el || !snapBoard) return;
    setActiveCol(activeIndex({ scrollLeft: el.scrollLeft, columnWidth: columnStep() - COLUMN_GAP, count: shownStageIds.length }));
  };
  const goToStage = (id) => {
    const index = shownStageIds.indexOf(id);
    if (index >= 0 && boardRef.current) boardRef.current.scrollTo({ left: index * columnStep(), behavior: "smooth" });
  };

  const [searchInput, setSearchInput] = useState(urlView.q || "");
  const board = usePipelineBoard({
    scope, phase, stages: stageIds, q: urlView.q, channel: urlView.channel, pilot: urlView.pilot, flag: urlView.flag, sort, assignee: urlView.assignee,
  });
  const { columns, meta, loading, refreshing, error, counters } = board;
  // After any action the board AND the open panel are re-read (silently: no spinner, nothing lost on screen).
  const reloadAll = () => { board.reload(); setPanelVersion((v) => v + 1); };

  const [statusDialog, setStatusDialog] = useState({ open: false, prospect: null, status: null, originalStatus: null, snapshot: null });
  const [statusExtra, setStatusExtra] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [revertedId, setRevertedId] = useState(null);
  const [noteFor, setNoteFor] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const [planFor, setPlanFor] = useState(null);
  const [reopenFor, setReopenFor] = useState(null);
  const pendingMoves = useRef(new Set());
  const [panelVersion, setPanelVersion] = useState(0);
  const pushedFiche = useRef(false);
  const lastFiche = useRef(null);

  // --- address & memory ---
  const patchUrl = (patch) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        if (v === undefined || v === null || v === "") next.delete(k); else next.set(k, v);
      }
      return next;
    }, { replace: true });
  };
  const remember = (patch) => {
    const next = { ...memory, ...patch };
    setMemory(next);
    writeMemory(user?.id, next);
  };
  const choose = (key, value) => {
    patchUrl({ [key]: value });
    remember({ [key]: value });
  };

  useEffect(() => {
    const t = setTimeout(() => { if ((urlView.q || "") !== searchInput.trim()) patchUrl({ q: searchInput.trim() }); }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const toggleStage = (id) => remember({ columns: { ...(memory.columns || {}), [id]: memory.columns?.[id] === false } });
  const showAll = () => remember({ columns: {} });

  const hiddenInAll = phase === "all" ? stageIds.filter((id) => !shownStageIds.includes(id)) : [];
  const shownCount = shownStageIds.reduce((n, id) => n + (columns[id]?.count || 0), 0);
  const hiddenCount = hiddenInAll.reduce((n, id) => n + (columns[id]?.count || 0), 0);
  const sheetCount = sheetFilterCount({ ...urlView, sort }, "activity");
  const filtersActive = !!(urlView.q || urlView.channel || urlView.pilot || urlView.flag || urlView.assignee);
  const clearFilters = () => { setSearchInput(""); patchUrl({ q: null, channel: null, pilot: null, flag: null, assignee: null }); };

  const userName = (id) => {
    if (!id) return null;
    if (meta.assignees[id]) return meta.assignees[id];
    if (id === user?.id) return user.name;
    return directory.find((u) => u.id === id)?.name || null;
  };

  // --- quick panel (?fiche=<id>) ---
  // The address is the single source of truth: Back closes the panel, a reload or a shared link reopens it, filters and phase stay.
  const openFiche = (prospect) => {
    if (!prospect || prospect.id === ficheId) return;
    const mode = ficheNavigation(ficheId);
    if (mode === "push") pushedFiche.current = true;
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set("fiche", prospect.id); return next; }, { replace: mode === "replace" });
  };
  const closeFiche = () => {
    if (pushedFiche.current) { pushedFiche.current = false; navigate(-1); } else patchUrl({ fiche: null });
  };
  useEffect(() => {
    // Back to the card that was open, so the keyboard user is where they were (the panel never traps focus).
    const previous = lastFiche.current;
    lastFiche.current = ficheId;
    if (!ficheId) pushedFiche.current = false;
    if (previous && !ficheId) setTimeout(() => document.querySelector(`[data-testid="kanban-card-${previous}"]`)?.focus(), 50);
  }, [ficheId]);

  // --- moves ---
  const flashReverted = (id) => {
    setRevertedId(id);
    setTimeout(() => setRevertedId((cur) => (cur === id ? null : cur)), 400);
  };

  const reconcileFailedMove = async (prospect, snapshot, err) => {
    const conflict = conflictInfo(err);
    const httpStatus = err?.response?.status;
    if (conflict?.currentStatus || httpStatus === 404) {
      try {
        const data = await board.refreshCards([prospect.id]);
        const shown = data.items[0];
        if (!shown) toast.error(errorMessage(err, "Ce prospect n'existe plus ou n'est plus dans votre périmètre."));
        else if (shownStageIds.includes(shown.status)) toast.error(`${prospect.pharmacy_name} a été déplacé entre-temps vers « ${STAGE_LABEL[shown.status] || shown.status} ».`);
        else toast.error(`${prospect.pharmacy_name} est maintenant dans « ${STAGE_LABEL[shown.status] || shown.status} », une colonne qui n'est pas affichée ici.`);
        return;
      } catch (e) { /* falls through to the plain revert below */ }
    }
    board.restore(snapshot);
    flashReverted(prospect.id);
    toast.error(errorMessage(err));
  };

  const applyStatus = async (prospect, target, originalStatus, snapshot, extra = {}) => {
    if (pendingMoves.current.has(prospect.id)) return;
    pendingMoves.current.add(prospect.id);
    try {
      await api.post(`/prospects/${prospect.id}/status`, { status: target, from_status: originalStatus, ...extra });
      toast.success(`Déplacé vers ${STAGE_LABEL[target]}`);
      reloadAll();
    } catch (e) {
      await reconcileFailedMove(prospect, snapshot, e);
      reloadAll();
    } finally {
      pendingMoves.current.delete(prospect.id);
    }
  };

  // Shared by the drag, the « Déplacer vers… » menu and « Rouvrir »: every manual stage change needs some context (why it moved, or a
  // scheduled meeting); the backend enforces the same rule. The card moves to the destination right away so it doesn't snap back
  // while the dialog is open; only a cancel reverts it.
  const requestMove = (prospect, target) => {
    if (!canChangeStatus || !prospect || prospect.status === target) return;
    if (SYSTEM_MANAGED_STAGES.includes(target)) {
      toast.error("Cette étape démarre automatiquement : utilisez « Lancer l'onboarding » sur la fiche du prospect.");
      return;
    }
    const snapshot = board.moveLocal(prospect.id, target);
    setStatusExtra(defaultStatusExtra(target));
    setStatusDialog({ open: true, prospect, status: target, originalStatus: prospect.status, snapshot });
  };

  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination || source.droppableId === destination.droppableId) return;
    const prospect = columns[source.droppableId]?.items.find((c) => c.id === draggableId);
    requestMove(prospect, destination.droppableId);
  };

  const closeDialog = () => setStatusDialog({ open: false, prospect: null, status: null, originalStatus: null, snapshot: null });

  const confirmStatusChange = async () => {
    if (!statusDialog.prospect || submitting) return;
    if (!isStatusExtraValid(statusDialog.status, statusExtra)) {
      toast.error("Merci de compléter les informations requises.");
      return;
    }
    const { prospect, status, originalStatus, snapshot } = statusDialog;
    setSubmitting(true);
    closeDialog();
    await applyStatus(prospect, status, originalStatus, snapshot, buildStatusExtraPayload(status, statusExtra));
    setSubmitting(false);
  };

  const cancelStatusChange = () => {
    const { prospect, snapshot } = statusDialog;
    closeDialog();
    if (!prospect) return;
    board.restore(snapshot);
    flashReverted(prospect.id);
  };

  const onQuick = async (id, prospect) => {
    if (id === "note") setNoteFor(prospect);
    else if (id === "plan") setPlanFor(prospect);
    else if (id === "assign") setAssignFor(prospect);
    else if (id === "reopen") setReopenFor(prospect);
    else if (id === "start_onboarding") {
      try {
        await api.post(`/prospects/${prospect.id}/start-onboarding`);
        toast.success("Onboarding lancé");
        board.refreshCards([prospect.id]).catch(() => {});
        reloadAll();
      } catch (e) {
        toast.error(errorMessage(e));
      }
    }
  };
  const afterQuick = (prospect) => { board.refreshCards([prospect.id]).catch(() => {}); reloadAll(); };

  const statusDialogTitle = statusDialog.status === "lost"
    ? "Motif de perte"
    : statusDialog.status === "meeting_scheduled"
      ? "Planifier le rendez-vous"
      : "Ajouter une interaction";

  // --- derived screen state ---
  const total = Object.values(columns).reduce((n, c) => n + c.count, 0);
  const noData = !loading && !error && total === 0;
  const skipped = (searchParams.get("skipped") || "").split(",").filter(Boolean);
  const toggleFlag = (flag) => choose("flag", urlView.flag === flag ? null : flag);
  const counterItems = [
    canViewTasks && phase !== "exit" ? { id: "todo", label: "À faire aujourd'hui ou en retard", active: urlView.flag === "late", onClick: () => toggleFlag("late") } : null,
    canViewTasks && phase !== "exit" ? { id: "no_action", label: "Aucune action visible", active: urlView.flag === "noaction", onClick: () => toggleFlag("noaction") } : null,
    phase !== "exit" ? { id: "dormant", label: "Inactifs (14 j)", active: urlView.flag === "dormant", onClick: () => toggleFlag("dormant") } : null,
  ].filter(Boolean);

  const phaseOptions = PHASES.map((p) => ({ id: p.id, label: p.label }));
  const scopeOptions = canScopeAll ? SCOPES.map((s) => ({ ...s, count: meta.scopeCounts ? meta.scopeCounts[s.id] : null })) : [];
  const toProspects = pipelineToProspects({ ...view, ...urlView }, user?.id);

  const columnsPicker = phase === "all" ? (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid="btn-pipeline-columns" className="justify-start">
          <Columns3 size={14} className="mr-2" />
          Colonnes{hiddenInAll.length > 0 ? ` (${shownStageIds.length}/${STAGES.length})` : ""}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-4">
        <div className="font-semibold text-sm text-slate-900 mb-3">Afficher / masquer les colonnes</div>
        <div className="max-h-80 overflow-y-auto scrollbar-thin space-y-4">
          {STAGE_GROUPS.map((group) => (
            <div key={group.id}>
              <div className="text-2xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{group.label}</div>
              <div className="space-y-0.5">
                {group.stageIds.map((id) => {
                  const stage = STAGES.find((st) => st.id === id);
                  return (
                    <label key={id} className="flex items-center gap-2.5 py-1.5 px-1 rounded hover:bg-slate-50 cursor-pointer" data-testid={`toggle-stage-${id}`}>
                      <Checkbox checked={shownStageIds.includes(id)} onCheckedChange={() => toggleStage(id)} />
                      <span className="text-sm text-slate-700">{stage.label}</span>
                      <span className="ml-auto text-xs text-slate-500 font-mono">{columns[id]?.count ?? "…"}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-end pt-3 mt-3 border-t border-slate-100">
          <button onClick={showAll} data-testid="btn-show-all-stages" className="text-xs text-jamora hover:underline">Tout afficher</button>
        </div>
      </PopoverContent>
    </Popover>
  ) : null;

  // A native <select> inside the phone sheet: its list is the phone's own picker, never a layer that the sheet could take for a click outside.
  const renderFilters = (native) => {
    const current = { assignee: urlView.assignee || "all", channel: urlView.channel || "all", pilot: urlView.pilot || "all", sort };
    const value = (key) => (native ? draft[key] ?? current[key] : current[key]);
    const change = (key) => (v) => {
      if (native) setDraft((d) => ({ ...d, [key]: v }));
      else if (key === "sort") choose("sort", v);
      else patchUrl({ [key]: v === "all" ? null : v });
    };
    return (
      <>
        {canScopeAll ? (
          <FilterSelect native={native} label="Responsable" testid="filter-assignee" className="sm:w-48" value={value("assignee")} onChange={change("assignee")}
            options={[{ id: "all", label: "Responsable : tous" }, ...directory.map((u) => ({ id: u.id, label: u.name }))]} />
        ) : null}
        <FilterSelect native={native} label="Canal d'acquisition" testid="filter-channel" className="sm:w-48" value={value("channel")} onChange={change("channel")}
          options={[{ id: "all", label: "Canal : tous" }, ...ACQUISITION_CHANNELS]} />
        <FilterSelect native={native} label="Pilote" testid="filter-pilot" className="sm:w-44" value={value("pilot")} onChange={change("pilot")} options={PILOT_OPTIONS} />
        <FilterSelect native={native} label="Trier par" testid="sort-select" className="sm:w-48" value={value("sort")} onChange={change("sort")} options={SORTS.map((o) => ({ id: o.id, label: `Trier : ${o.label}` }))} />
      </>
    );
  };
  // Desktop and tablet: the four filters live behind one button at the right end of the « À traiter » row (the phone has its own sheet).
  const filtersButton = !isPhone ? (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-8 gap-2 px-3 text-xs" data-testid="btn-open-filters-popover" aria-label="Filtres">
          <SlidersHorizontal size={14} aria-hidden />
          Filtres
          {sheetCount > 0 ? <span className="font-mono" data-testid="filters-badge">{sheetCount}</span> : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-3 [&>*]:!w-full" data-testid="pipeline-filters">
        {renderFilters(false)}
        {filtersActive ? <Button variant="ghost" size="sm" className="w-full" onClick={clearFilters} data-testid="btn-clear-filters"><X size={14} className="mr-1" />Effacer les filtres</Button> : null}
      </PopoverContent>
    </Popover>
  ) : null;
  const openFilters = () => { setDraft({}); setFiltersOpen(true); };
  const applyFilters = () => {
    const patch = {};
    for (const k of ["assignee", "channel", "pilot"]) if (draft[k] !== undefined) patch[k] = draft[k] === "all" ? null : draft[k];
    if (draft.sort !== undefined && draft.sort !== sort) { patch.sort = draft.sort; remember({ sort: draft.sort }); }
    setFiltersOpen(false);
    if (Object.keys(patch).length) patchUrl(patch);
  };

  return (
    <div className="space-y-4" data-testid="pipeline-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl text-slate-900">Pipeline</h1>
          <p className="text-sm text-slate-600" data-testid="pipeline-subtitle">
            {loading || error ? "Prospects par étape" : (
              <>
                {plural(shownCount, "prospect", "prospects")}
                {hiddenCount > 0 ? ` · ${plural(hiddenCount, "dans une colonne masquée", "dans des colonnes masquées")}` : ""}
                {canChangeStatus && total > 0 ? (isPhone ? " · menu « … » pour déplacer une carte" : " · glissez la poignée ou utilisez le menu pour déplacer une carte") : ""}
              </>
            )}
            {refreshing ? <Loader2 size={12} className="ml-2 inline animate-spin text-slate-500" aria-label="Actualisation" /> : null}
          </p>
          {!canChangeStatus ? (
            <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-slate-600" data-testid="pipeline-readonly">
              <Lock size={12} aria-hidden /> Lecture seule : vous ne pouvez pas déplacer les cartes.
            </p>
          ) : null}
        </div>
        <div className={`flex gap-2 w-full md:w-auto ${isPhone ? "flex-row" : "flex-col sm:flex-row"}`}>
          {!isPhone ? <Button variant="outline" asChild data-testid="btn-see-in-list"><Link to={`/prospects?${toProspects.toString()}`}><ListFilter size={14} className="mr-2" />Voir dans la liste</Link></Button> : null}
          {!isPhone ? columnsPicker : null}
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} aria-hidden />
            <Input
              data-testid="input-pipeline-search"
              aria-label="Rechercher un prospect"
              placeholder="Rechercher un prospect…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
            />
          </div>
          {isPhone ? (
            <Button variant="outline" className="relative h-10 shrink-0 px-3" onClick={openFilters} data-testid="btn-open-filters" aria-label="Filtres et options">
              <SlidersHorizontal size={16} aria-hidden />
              {sheetCount > 0 ? <span className="ml-1.5 font-mono text-xs" data-testid="filters-badge">{sheetCount}</span> : null}
            </Button>
          ) : null}
        </div>
      </div>

      {skipped.length > 0 ? (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-slate-800" role="status" data-testid="skipped-banner">
          <span>Les filtres suivants de la liste n'existent pas dans le Pipeline et ne sont pas appliqués : {skipped.map((k) => SKIPPED_LABELS[k] || k).join(", ")}.</span>
          <button type="button" aria-label="Fermer ce message" onClick={() => patchUrl({ skipped: null })} className="shrink-0 rounded p-1 hover:bg-sky-100"><X size={14} /></button>
        </div>
      ) : null}

      <div className={isPhone ? "space-y-2" : "flex flex-wrap items-center gap-3"}>
        <Segmented label="Phase" value={phase} options={phaseOptions} onChange={(v) => choose("phase", v)} testid="phase" scroll={isPhone} />
        {canScopeAll ? <Segmented label="Périmètre" value={scope} options={scopeOptions} onChange={(v) => choose("scope", v)} testid="scope" scroll={isPhone} /> : null}
      </div>

      {counterItems.length > 0 ? (
        <CountersBand
          items={counterItems}
          state={counters}
          onRetry={board.reload}
          trailing={filtersButton}
        />
      ) : filtersButton ? <div className="flex justify-end">{filtersButton}</div> : null}

      {filtersActive ? (
        <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="pipeline-chips">
          {urlView.q ? <Chip label={`Recherche : « ${urlView.q} »`} onRemove={() => { setSearchInput(""); patchUrl({ q: null }); }} testid="chip-q" /> : null}
          {urlView.flag ? <Chip label={flagLabel(urlView.flag)} onRemove={() => patchUrl({ flag: null })} testid="chip-flag" /> : null}
          {urlView.channel ? <Chip label={`Canal : ${ACQUISITION_CHANNELS.find((c) => c.id === urlView.channel)?.label || urlView.channel}`} onRemove={() => patchUrl({ channel: null })} testid="chip-channel" /> : null}
          {urlView.pilot ? <Chip label={PILOT_OPTIONS.find((o) => o.id === urlView.pilot)?.label} onRemove={() => patchUrl({ pilot: null })} testid="chip-pilot" /> : null}
          {urlView.assignee ? <Chip label={`Responsable : ${userName(urlView.assignee) || "Utilisateur"}`} onRemove={() => patchUrl({ assignee: null })} testid="chip-assignee" /> : null}
        </div>
      ) : null}

      {loading ? (
        <div className="flex gap-4 overflow-hidden" data-testid="pipeline-loading" aria-busy="true" aria-label="Chargement du pipeline">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="min-w-[240px] w-[240px] flex-shrink-0 space-y-2">
              <Skeleton className="h-12 w-full rounded-xl" />
              <Skeleton className="h-28 w-full rounded-xl" />
              <Skeleton className="h-28 w-full rounded-xl" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-6 text-center" data-testid="pipeline-error">
          <AlertCircle className="mx-auto text-red-600" size={24} aria-hidden />
          <p className="mt-2 font-medium text-slate-900">Impossible de charger le pipeline</p>
          <p className="text-sm text-slate-600">Vérifiez votre connexion puis réessayez.</p>
          <Button className="mt-3" variant="outline" onClick={board.reload} data-testid="btn-pipeline-retry">Réessayer</Button>
        </div>
      ) : noData ? (
        <EmptyState
          filtersActive={filtersActive} phase={phase} scope={scope} canScopeAll={canScopeAll} allCount={meta.scopeCounts?.all || 0}
          canCreate={canCreateProspect} canImport={canImport} onClear={clearFilters} onAll={() => choose("scope", "all")} navigate={navigate}
        />
      ) : (
        <>
        {snapBoard ? (
          <StageTabs
            stages={shownStageIds.map((id) => STAGES.find((st) => st.id === id))}
            counts={Object.fromEntries(shownStageIds.map((id) => [id, columns[id]?.count]))}
            activeId={shownStageIds[activeCol]} onSelect={goToStage}
          />
        ) : null}
        <DragDropContext onDragEnd={onDragEnd}>
          <div
            ref={boardRef} onScroll={onBoardScroll} data-layout={layout}
            className={`flex gap-4 overflow-x-auto pb-4 scrollbar-thin items-start ${snapBoard ? "snap-x snap-mandatory" : ""}`} data-testid="pipeline-board"
          >
            {shownStageIds.map((id) => {
              const stage = STAGES.find((st) => st.id === id);
              const column = columns[id] || { count: 0, items: [], next: null };
              return (
                <Droppable droppableId={id} key={id}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      data-testid={`kanban-column-${id}`}
                      className={`${COLUMN_SIZE[layout]} flex-shrink-0 rounded-xl transition flex flex-col ${snapBoard ? "snap-start" : ""} ${isPhone ? "" : "max-h-[calc(100dvh-20rem)]"} min-h-[16rem] ${snapshot.isDraggingOver ? "bg-jamora/5 ring-2 ring-jamora/30" : ""}`}
                    >
                      <div className="bg-white border border-slate-200 rounded-xl p-3 shrink-0">
                        <div className="flex items-center justify-between">
                          <span className={`text-xs font-semibold uppercase tracking-wider px-2 py-1 rounded stage-${id}`}>{stage.label}</span>
                          <span className="text-xs font-mono text-slate-600" data-testid={`kanban-count-${id}`}>{column.count}</span>
                        </div>
                      </div>
                      <div className="mt-2 space-y-2 min-h-[40px] flex-1 overflow-y-auto scrollbar-thin pr-1">
                        {column.items.map((p, idx) => (
                          <PipelineCard
                            key={p.id}
                            prospect={p}
                            index={idx}
                            today={todayIso()}
                            canMove={canChangeStatus}
                            can={can}
                            canAssign={canAssign}
                            tasksVisible={meta.tasksVisible}
                            assigneeName={userName(p.assigned_to)}
                            customerBadge={customerBadges[p.id]}
                            reverted={p.id === revertedId}
                            selected={p.id === ficheId}
                            dragOff={isPhone}
                            onMove={requestMove}
                            onQuick={onQuick}
                            onOpenPanel={openFiche}
                          />
                        ))}
                        {provided.placeholder}
                        {column.items.length === 0 && !snapshot.isDraggingOver ? (
                          <div className="text-xs text-slate-600 text-center py-6 border border-dashed border-slate-200 rounded-lg">Aucun prospect</div>
                        ) : null}
                        {column.next ? (
                          <Button variant="outline" size="sm" className="w-full" disabled={column.loadingMore} onClick={() => board.loadMore(id)} data-testid={`load-more-${id}`}>
                            {column.loadingMore ? "Chargement…" : `Voir plus (${column.count - column.items.length} restants)`}
                          </Button>
                        ) : null}
                        {column.loadMoreFailed ? <p className="text-xs text-red-700" role="alert">Impossible de charger la suite. Réessayez.</p> : null}
                        {canCreateProspect && REOPEN_STAGES.includes(id) ? (
                          <button
                            onClick={() => navigate(`/prospects/nouveau?status=${id}`)}
                            data-testid={`btn-add-prospect-${id}`}
                            className="w-full flex items-center gap-1.5 text-xs text-slate-600 hover:text-jamora py-1.5 px-1 rounded hover:bg-slate-50"
                          >
                            <Plus size={13} />
                            Ajouter un prospect
                          </button>
                        ) : null}
                      </div>
                    </div>
                  )}
                </Droppable>
              );
            })}
          </div>
        </DragDropContext>
        </>
      )}

      {isPhone ? (
        <Dialog open={filtersOpen} onOpenChange={setFiltersOpen}>
          <DialogContent data-testid="filters-sheet">
            <DialogHeader><DialogTitle>Filtres et options</DialogTitle></DialogHeader>
            <div className="space-y-3" data-testid="pipeline-filters">
              {renderFilters(true)}
              {columnsPicker}
              <Button variant="outline" asChild className="w-full" data-testid="btn-see-in-list"><Link to={`/prospects?${toProspects.toString()}`}><ListFilter size={14} className="mr-2" />Voir dans la liste</Link></Button>
            </div>
            <DialogFooter>
              {filtersActive ? <Button variant="ghost" onClick={clearFilters} data-testid="btn-clear-filters"><X size={14} className="mr-1" />Effacer les filtres</Button> : null}
              <Button onClick={applyFilters} data-testid="btn-close-filters">Afficher les résultats</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}

      <QuickNoteDialog prospect={noteFor} onClose={() => setNoteFor(null)} onDone={afterQuick} />
      <ReopenDialog prospect={reopenFor} onClose={() => setReopenFor(null)} onChoose={(p, stage) => { setReopenFor(null); requestMove(p, stage); }} />
      <QuickAssignDialog prospect={assignFor} directory={directory} onClose={() => setAssignFor(null)} onDone={afterQuick} />
      <TaskFormSheet
        open={!!planFor} onOpenChange={(v) => { if (!v) setPlanFor(null); }} task={null}
        canScopeAll={canScopeAllTasks} canEdit={hasPermission(user, "tasks:edit")} directory={directory}
        onSaved={() => { const p = planFor; setPlanFor(null); if (p) afterQuick(p); }}
        lockedProspect={planFor ? { id: planFor.id, pharmacy_name: planFor.pharmacy_name } : null}
      />

      <Dialog
        open={statusDialog.open}
        onOpenChange={(v) => { if (!v) cancelStatusChange(); }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{statusDialogTitle}</DialogTitle>
            <p className="text-sm text-slate-600 -mt-1">{statusDialog.prospect?.pharmacy_name}</p>
            <div className="flex items-center gap-2 pt-1">
              <span className={`text-2xs font-semibold uppercase tracking-wider px-2 py-1 rounded stage-${statusDialog.originalStatus}`}>
                {STAGE_LABEL[statusDialog.originalStatus]}
              </span>
              <ArrowRight size={14} className="text-slate-400 shrink-0" />
              <span className={`text-2xs font-semibold uppercase tracking-wider px-2 py-1 rounded stage-${statusDialog.status}`}>
                {STAGE_LABEL[statusDialog.status]}
              </span>
            </div>
          </DialogHeader>
          <StatusExtraFields status={statusDialog.status} value={statusExtra} onChange={setStatusExtra} />
          <DialogFooter>
            <Button variant="ghost" onClick={cancelStatusChange}>
              Annuler
            </Button>
            <Button
              data-testid="btn-confirm-status"
              disabled={submitting}
              onClick={confirmStatusChange}
            >
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    {ficheId ? (
      <QuickPanel
        id={ficheId} mode={mode} today={todayIso()} can={can} canAssign={canAssign} canMove={canChangeStatus} withTasks={canViewTasks}
        seed={Object.values(columns).flatMap((c) => c.items).find((c) => c.id === ficheId) || null}
        customerBadge={customerBadges[ficheId]} nameOf={userName} version={panelVersion}
        onClose={closeFiche} onQuick={onQuick} onMove={requestMove}
      />
    ) : null}
    </div>
  );
}

function Chip({ label, onRemove, testid }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-slate-700" data-testid={testid}>
      {label}
      <button type="button" aria-label={`Retirer : ${label}`} onClick={onRemove} className="rounded-full p-0.5 hover:bg-slate-200"><X size={12} /></button>
    </span>
  );
}

function EmptyState({ filtersActive, phase, scope, canScopeAll, allCount, canCreate, canImport, onClear, onAll, navigate }) {
  const box = "rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center";
  if (filtersActive) {
    return (
      <div className={box} data-testid="pipeline-no-match">
        <p className="font-semibold text-slate-900">Aucun prospect ne correspond</p>
        <p className="mt-1 text-sm text-slate-600">Essayez un autre nom, une autre ville, un autre numéro ou retirez un filtre.</p>
        <Button className="mt-4" variant="outline" onClick={onClear} data-testid="btn-no-match-clear">Effacer les filtres</Button>
      </div>
    );
  }
  if (phase === "exit") {
    return <div className={box} data-testid="pipeline-empty-exit"><p className="font-semibold text-slate-900">Aucun prospect perdu</p><p className="mt-1 text-sm text-slate-600">Les prospects perdus apparaîtront ici avec leur motif.</p></div>;
  }
  const phaseName = phaseOf(phase).label.toLowerCase();
  if (canScopeAll && scope === "me") {
    return (
      <div className={box} data-testid="pipeline-empty-me">
        <p className="font-semibold text-slate-900">Aucun prospect ne vous est attribué{phase === "all" ? "" : ` en ${phaseName}`}</p>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          {allCount > 0 ? <Button variant="outline" onClick={onAll} data-testid="btn-empty-see-all">Voir tous les prospects</Button> : null}
          {canCreate ? <Button onClick={() => navigate("/prospects/nouveau")} data-testid="btn-empty-create"><Plus size={14} className="mr-1.5" />Créer un prospect</Button> : null}
        </div>
      </div>
    );
  }
  return (
    <div className={box} data-testid="pipeline-empty">
      <p className="font-semibold text-slate-900">Aucun prospect{phase === "all" ? " pour le moment" : ` en ${phaseName}`}</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-slate-600">
        {canCreate || canImport
          ? "Ajoutez votre premier prospect pour commencer à suivre votre pipeline commercial."
          : "Aucun prospect ne vous est attribué. Demandez à un responsable de vous en attribuer."}
      </p>
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        {canCreate ? <Button onClick={() => navigate("/prospects/nouveau")} data-testid="btn-empty-create"><Plus size={14} className="mr-1.5" />Nouveau prospect</Button> : null}
        {canImport ? <Button variant="outline" onClick={() => navigate("/import")} data-testid="btn-empty-import">Importer</Button> : null}
      </div>
    </div>
  );
}
